// File: src/components/games/GameListPage.js
import React, { useState, useEffect } from "react";
import { Container, Card, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import axios from "axios";

function GameListPage({ activeSeason }) {
    const [games, setGames] = useState([]);
    const [user, setUser] = useState(null);
    const [loadingGames, setLoadingGames] = useState(true);

    useEffect(() => {
        axios.get('/api/users/my-details/', {
            withCredentials: true,
        })
            .then(response => {
                setUser(response.data);
            })
            .catch(error => {
                console.error('Error fetching user profile:', error);
            });
    }, []);

    useEffect(() => {
        if (!activeSeason?.id) {
            setGames([]);
            setLoadingGames(false);
            return;
        }

        setLoadingGames(true);
        axios
            .get(`/api/games/seasons/${activeSeason.id}/`, {
                params: { is_played: false },
                withCredentials: true,
            })
            .then((response) => {
                setGames(Array.isArray(response.data) ? response.data : []);
            })
            .catch((error) => {
                console.error("Error fetching games:", error);
                setGames([]);
            })
            .finally(() => {
                setLoadingGames(false);
            });
    }, [activeSeason?.id]);

    const handleAttendingToggle = async (gameId, participantId, currentAttending) => {
        const attendingUrl = `/api/games/${gameId}/attending/`;
        try {
            const response = await axios.patch(
                attendingUrl,
                { attending: !currentAttending },
                {
                    withCredentials: true,
                }
            );

            if (response.status === 200) {
                const updatedGames = games.map((game) => {
                    if (game.id === gameId) {
                        return {
                            ...game,
                            players: game.players.map((p) =>
                                p.id === participantId ? { ...p, attending: !currentAttending } : p
                            ),
                        };
                    }
                    return game;
                });
                setGames(updatedGames);
            }
        } catch (error) {
            console.error("Error updating attending status:", error);
        }
    };

    return (
        <Container className="p-0">
            <Card className="h-100">
                {/* Card Header */}
                <Card.Header className="d-sm-flex align-items-center text-center justify-content-sm-between border-0 pb-0">
                    <h1 className="h4 card-title">Upcoming Games</h1>
                </Card.Header>
                {/* Card Body */}
                <Card.Body>
                    <Container>
                        {loadingGames ? (
                            <p>Loading games...</p>
                        ) : games.length === 0 ? (
                            <p className="mb-0 text-muted">No games currently scheduled for this Season.</p>
                        ) : (
                            <Row className="g-4">
                                {games.map((game) => {
                                    // Extract participants as in your previous code
                                    const homeParticipant =
                                        game.teams?.find((p) => p.is_home) || {};
                                    const awayParticipant =
                                        game.teams?.find((p) => p.is_away) || {};
                                    const isByeGame = game.game_type === 'bye';
                                    const userParticipant = game.players?.find(
                                        (p) => p.content_type === "player" && user?.players?.some(player => player.id === p.object_id)
                                    ) || {};
                                    const canToggleAttendance = Boolean(userParticipant.id);
                                    return (
                                        <Col key={game.id} sm={6} xl={4}>
                                            <Card className="h-100">
                                                <div className="position-relative">
                                                    <div className="position-absolute top-0 start-0 m-2" style={{ zIndex: 10 }}>
                                                        <span className={`badge ${game.game_type === 'preseason' ? 'bg-info' :
                                                            game.game_type === 'regular' ? 'bg-primary' :
                                                                game.game_type === 'makeup' ? 'bg-warning' :
                                                                    game.game_type === 'playoff' ? 'bg-success' : 'bg-secondary'
                                                            }`}>
                                                            {game.game_type ? game.game_type.charAt(0).toUpperCase() + game.game_type.slice(1) : 'Unknown'}
                                                        </span>
                                                    </div>
                                                    {/* 50×50 Image Composition */}
                                                    <div
                                                        style={{
                                                            width: '100%',
                                                            height: '200px',
                                                            margin: 'auto',
                                                            display: 'block',
                                                            position: 'relative',
                                                            overflow: 'hidden',
                                                            borderRadius: '8px',
                                                        }}
                                                    >
                                                        {isByeGame ? (
                                                            homeParticipant.participant_logo ? (
                                                                <img
                                                                    src={homeParticipant.participant_logo}
                                                                    alt={`${homeParticipant.participant_name} logo`}
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '200px',
                                                                        objectFit: 'cover',
                                                                    }}
                                                                />
                                                            ) : (
                                                                // Placeholder for bye games
                                                                <div
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '200px',
                                                                        backgroundColor: '#f8f9fa',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                    }}
                                                                >
                                                                    <i className="bi bi-people-fill" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                                                                </div>
                                                            )
                                                        ) : (
                                                            <>
                                                                {homeParticipant.participant_logo ? (
                                                                    // Top half – Home Team logo
                                                                    <img
                                                                        src={homeParticipant.participant_logo}
                                                                        alt={`${homeParticipant.participant_name} logo`}
                                                                        style={{
                                                                            width: '100%',
                                                                            height: '200px',
                                                                            objectFit: 'cover',
                                                                            position: 'absolute',
                                                                            left: 0, // Ensure image is aligned to the left
                                                                            top: 0,
                                                                            clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)',
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    // Placeholder for top half – Blue
                                                                    <div
                                                                        style={{
                                                                            width: '100%',
                                                                            height: '50%',
                                                                            backgroundColor: 'blue',
                                                                            position: 'absolute',
                                                                            top: 0,
                                                                        }}
                                                                    ></div>
                                                                )}
                                                                {awayParticipant.participant_logo ? (
                                                                    // Bottom half – Away Team logo
                                                                    <img
                                                                        src={awayParticipant.participant_logo}
                                                                        alt={`${awayParticipant.participant_name} logo`}
                                                                        style={{
                                                                            width: '100%',
                                                                            height: '200px',
                                                                            objectFit: 'cover',
                                                                            position: 'absolute',
                                                                            left: 0, // Ensure image is aligned to the left
                                                                            bottom: 0,
                                                                            clipPath: 'polygon(0 50%, 100% 50%, 100% 100%, 0 100%)',
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    // Placeholder for bottom half – Red
                                                                    <div
                                                                        style={{
                                                                            width: "100%",
                                                                            height: "100%",
                                                                            backgroundColor: "red",
                                                                            position: "absolute",
                                                                            bottom: 0,
                                                                        }}
                                                                    ></div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className={`badge ${game.is_played ? 'bg-success' : 'bg-danger'} text-white mt-2 me-2 position-absolute top-0 end-0`}>
                                                        {game.is_played ? 'Played' : 'Upcoming'}
                                                    </div>
                                                </div>
                                                <Card.Body className="position-relative pt-0">
                                                    <div className="btn btn-xs btn-primary mt-n3">
                                                        {game.league_name}
                                                    </div>
                                                    {isByeGame ? (
                                                        // Simplified display for bye games
                                                        <div className="text-center mb-2">
                                                            <h6 className="mt-3">
                                                                <Link className="nav-link" to={`/games/${game.id}/`}>
                                                                    {homeParticipant.participant_name || 'Team'} (BYE)
                                                                </Link>
                                                            </h6>
                                                        </div>
                                                    ) : (
                                                        // Regular game display
                                                        <h6 className="mt-3">
                                                            <Link className="nav-link" to={`/games/${game.id}/`}>
                                                                {game.home_team} vs. {game.away_team}
                                                            </Link>
                                                        </h6>
                                                    )}
                                                    <p className="mb-0 small">
                                                        <i className="bi bi-calendar-check pe-1"></i>{" "}
                                                        {game.scheduled_date_only} at{" "}
                                                        {game.scheduled_time_only}
                                                    </p>
                                                    <p className="small">
                                                        <i className="bi bi-geo-alt pe-1"></i>{" "}
                                                        {game.location?.name}
                                                    </p>
                                                    <ul className="avatar-group list-unstyled align-items-center mb-0">
                                                        {(() => {
                                                            // const attendingPlayers = game.participants
                                                            //     .filter(p => p.content_type === 'player' && p.attending);
                                                            const attendingPlayers = game.players?.filter(p => p.attending) || [];
                                                            const displayed = attendingPlayers.slice(0, 3);
                                                            const remaining = attendingPlayers.length - displayed.length;

                                                            return (
                                                                <>
                                                                    {displayed.map((p, idx) => (
                                                                        <li key={`${p.id}-${idx}`} className="avatar avatar-xs">
                                                                            <img
                                                                                className="avatar-img rounded-circle"
                                                                                src={
                                                                                    p.participant_logo ||
                                                                                    `/static/images/kb_static/lemont_rec_leagues_circle.png`
                                                                                }
                                                                                alt={p.participant_name}
                                                                            />
                                                                        </li>
                                                                    ))}

                                                                    {remaining > 0 && (
                                                                        <li className="avatar avatar-xs">
                                                                            <div className="avatar-img rounded-circle bg-primary">
                                                                                <span className="smaller text-white position-absolute top-50 start-50 translate-middle">
                                                                                    +{remaining}
                                                                                </span>
                                                                            </div>
                                                                        </li>
                                                                    )}

                                                                    <li className="ms-3">
                                                                        <small>
                                                                            {attendingPlayers.length}{' '}
                                                                            {attendingPlayers.length === 1 ? 'attending' : 'attending'}
                                                                        </small>
                                                                    </li>
                                                                </>
                                                            );
                                                        })()}
                                                    </ul>
                                                    <div className="d-flex mt-3 justify-content-between">
                                                        {/* Interested Button */}
                                                        <div className="w-100">
                                                            <input
                                                                type="checkbox"
                                                                className="btn-check"
                                                                id={`Interested-${game.id}-${userParticipant.id || "none"}`}
                                                                checked={userParticipant.attending || false}  // Default to false if undefined
                                                                disabled={!canToggleAttendance}
                                                                onChange={() => handleAttendingToggle(game.id, userParticipant.id, userParticipant.attending)}
                                                            />
                                                            <label
                                                                className={`btn btn-sm ${userParticipant.attending ? 'btn-success' : 'btn-outline-success'} ${!canToggleAttendance ? 'disabled' : ''}`}
                                                                htmlFor={`Interested-${game.id}-${userParticipant.id || "none"}`}
                                                            >
                                                                <i className="fa-solid fa-thumbs-up me-1"></i> {userParticipant.attending ? "Attending" : "Not Attending"}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        )}
                    </Container>
                </Card.Body>
            </Card>
        </Container>
    );
}

export default GameListPage;
